import { useQueryClient } from '@tanstack/react-query';
import { Button, Loader, Stack, toast } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { usePatchPlotPortfolioSubscription } from '@/api/rest/resources/plotPortfolioSubscriptions';
import { Form } from '@/components';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import {
  PLOT_PORTFOLIO_SUBSCRIPTIONS_QUERY_KEY,
  usePlotPortfolioSubscription,
} from '@/hooks/usePlotPortfolioSubscriptions';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import {
  PlotPortfolioSubscriptionFormFields,
  PlotPortfolioSubscriptionFormValues,
} from '../components/PlotPortfolioSubscriptionFormFields';
import { SubscriptionStatusFormField } from '../components/SubscriptionStatusFormField';

type EditPlotPortfolioSubscriptionFormValues = PlotPortfolioSubscriptionFormValues & {
  status: string;
};

export const EditPlotPortfolioSubscriptionPage = () => {
  const { t } = useTranslation();
  const { organizationId, plotPortfolioSubscriptionId } = useParams();

  if (isNullish(organizationId) || isNullish(plotPortfolioSubscriptionId)) {
    throw new Error('Missing organizationId or plotPortfolioSubscriptionId route param');
  }

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      showBreadcrumbs={false}
      navBarProps={{
        title: t('shared.ndiOrganizationSubscriptions.editPlotPortfolioPage.title'),
        backPath: buildPath(paths.staff.organizationNdiSubscriptions, { pathParams: { organizationId } }),
        showSearchBar: false,
      }}
    >
      <Stack className='px-48 py-12 gap-6 max-w-[720px]'>
        <ErrorBoundary fallback={null}>
          <SuspenseBoundary
            fallback={
              <Stack centerMain>
                <Loader />
              </Stack>
            }
          >
            <EditPlotPortfolioSubscriptionForm
              organizationId={organizationId}
              plotPortfolioSubscriptionId={plotPortfolioSubscriptionId}
            />
          </SuspenseBoundary>
        </ErrorBoundary>
      </Stack>
    </SignedInPageLayout>
  );
};

const EditPlotPortfolioSubscriptionForm = ({
  organizationId,
  plotPortfolioSubscriptionId,
}: {
  organizationId: string;
  plotPortfolioSubscriptionId: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: plotPortfolioSubscription } = usePlotPortfolioSubscription(plotPortfolioSubscriptionId);
  const patchMutation = usePatchPlotPortfolioSubscription();

  const form = useForm<EditPlotPortfolioSubscriptionFormValues>({
    defaultValues: {
      display_name: plotPortfolioSubscription.display_name,
      start_date: plotPortfolioSubscription.start_date.slice(0, 10),
      end_date: plotPortfolioSubscription.end_date.slice(0, 10),
      status: plotPortfolioSubscription.status,
      initial_setup_cost_credits: String(plotPortfolioSubscription.initial_setup_cost_credits),
      plot_creation_cost_credits: String(plotPortfolioSubscription.plot_creation_cost_credits),
      max_plots:
        plotPortfolioSubscription.max_plots !== null && plotPortfolioSubscription.max_plots !== undefined
          ? String(plotPortfolioSubscription.max_plots)
          : '',
      max_hectares:
        plotPortfolioSubscription.max_hectares !== null && plotPortfolioSubscription.max_hectares !== undefined
          ? String(plotPortfolioSubscription.max_hectares)
          : '',
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await patchMutation.mutateAsync({
        pathVariables: { id: plotPortfolioSubscriptionId },
        bodyData: {
          display_name: values.display_name,
          start_date: values.start_date,
          end_date: values.end_date,
          status: values.status,
          initial_setup_cost_credits: Number(values.initial_setup_cost_credits),
          plot_creation_cost_credits: Number(values.plot_creation_cost_credits),
          max_plots: values.max_plots === '' ? null : Number(values.max_plots),
          max_hectares: values.max_hectares === '' ? null : Number(values.max_hectares),
        },
      });

      queryClient.invalidateQueries({ queryKey: [PLOT_PORTFOLIO_SUBSCRIPTIONS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.toast.updateSuccess') });
      navigate(buildPath(paths.staff.organizationNdiSubscriptions, { pathParams: { organizationId } }));
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
        <Stack spacing={4} className='p-6 rounded-2xl bg-white-100 border border-neutral-black-12'>
          <span className='typography-h4'>
            {t('shared.ndiOrganizationSubscriptions.editPage.sections.subscription')}
          </span>
          {/* react-hook-form's Control is invariant, so a form typed for the combined values isn't
              structurally assignable to one typed for a subset of them even though every field it
              needs is actually present. Safe here since it's the same form instance. */}
          <PlotPortfolioSubscriptionFormFields
            control={form.control as unknown as Control<PlotPortfolioSubscriptionFormValues>}
          />
          <SubscriptionStatusFormField control={form.control} />
        </Stack>

        <Button
          type='submit'
          className='self-end w-fit px-6'
          loading={patchMutation.isPending}
          disabled={patchMutation.isPending}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
